import { Color, GridHelper, Object3D, Quaternion, Vector3 } from "three";
import { OrbitControls } from "three/examples/jsm/controls/OrbitControls.js";
import { globalCommandManager } from "../commands/Command";
import { DeleteCommand } from "../commands/DeleteCommand";
import { TransformCommand } from "../commands/TransformCommand";
import { Engine, Entity, registerEvents } from "../engine/Engine";
import { SugarModel } from "../loader/SugarModel";
import {
  pickIntersectionForSceneSelection,
  raycast,
  raycastOnPlaneFromScreen,
} from "../physics/PhysicsUtil";
import { logger } from "../util/Logger";

@registerEvents(
  "dragstart",
  "dragover",
  "pointerdown",
  "pointermove",
  "pointerup",
  "keyup",
  "keydown",
  "model-selected",
  "model-created"
)
export class EditorTransformController extends Entity {
  private _selectedModel?: Object3D;
  private _mousedownModel?: Object3D;
  private _orbitControls?: OrbitControls;
  private _orbitEnabled = false;
  private _transformEnabled = false;
  private _models: SugarModel[] = [];

  private _onMouseupTransformControlsBindInstance;
  private _onMousedownTransformControlsBindInstance;
  private _onOrbitChangeInstance;
  private _onOrbitEndInstace;

  private _gridHelper;

  constructor(orbit: OrbitControls) {
    super();
    this._orbitControls = orbit;

    //bindInstance
    this._onMouseupTransformControlsBindInstance =
      this.onMouseupTransformControls.bind(this);
    this._onMousedownTransformControlsBindInstance =
      this.onMousedownTransformControls.bind(this);

    this._onOrbitChangeInstance = this.onOrbitChange.bind(this);
    this._onOrbitEndInstace = this.onOrbitEnd.bind(this);

    this._gridHelper = new GridHelper(
      10,
      100,
      new Color(0, 0, 0),
      new Color(0.5, 0.5, 0.5)
    );
  }

  begin(): void {
    Engine.activeEngine!.activeScene!.add(
      Engine.activeEngine!.transformControls.getHelper()
    );
  }

  onModelCreatedOccured(e: { detail: Object3D }) {
    logger.info("ModelAddedOccured from EditorController", e.detail);
    e.detail.addEventListener("added", () => {
      if (!this._models.includes(e.detail as SugarModel))
        this._models.push(e.detail as SugarModel);
    });

    e.detail.addEventListener("removed", () => {
      this._models.filter((m) => m !== e.detail);
    });
    this._models.push(e.detail as SugarModel);
    this._selectedModel = e.detail as SugarModel;
  }

  onPointerdownOccured(event: MouseEvent) {
    if (this._transformEnabled || this._orbitEnabled) return;
    const casts = raycast(event, this._models);
    const cast = pickIntersectionForSceneSelection(casts);
    if (!cast) return;

    this._mousedownModel = cast.object;
    logger.info("caste object", this._mousedownModel);
  }

  onPointerupOccured(event: MouseEvent) {
    if (this._transformEnabled || this._orbitEnabled) return;
    //mark as select

    logger.info(this._mousedownModel, this._selectedModel);
    if (
      this._mousedownModel /*&& this._mousedownModel !== this._selectedModel*/
    ) {
      Engine.activeEngine!.transformControls.enabled = true;
      Engine.activeEngine!.transformControls.attach(this._mousedownModel);
      this.engine!.dispatchCustomEvent("model-selected", {
        detail: this._mousedownModel,
      });
    } else if (!this._mousedownModel && this._selectedModel) {
      Engine.activeEngine!.transformControls.detach();
      logger.info("model-deselected");
      this.engine!.dispatchCustomEvent("model-deselected", {
        detail: this._selectedModel,
      });
      Engine.activeEngine!.transformControls.enabled = false;
    }

    this._selectedModel = this._mousedownModel;
    this._mousedownModel = undefined;
  }

  onDragoverOccured(event: DragEvent) {
    if (!this._enabled) return;
    event.preventDefault();
    if (!this._selectedModel) return;

    const pos = raycastOnPlaneFromScreen(
      event,
      { position: new Vector3(), normal: new Vector3(0, 1, 0) },
      this.engine!.activeCamera!.instance,
      Engine.activeEngine!.renderer.instance.domElement
    );
    this._selectedModel.position.copy(pos);
    this._selectedModel.updateWorldMatrix(true, true);
  }

  onKeyupOccured(event: KeyboardEvent) {
    if (!this._enabled) return;
    if (event.key === "w" && this._selectedModel) {
      Engine.activeEngine!.transformControls.setMode("translate");
    } else if (event.key === "e" && this._selectedModel) {
      Engine.activeEngine!.transformControls.setMode("rotate");
    } else if (event.key === "r" && this._selectedModel) {
      Engine.activeEngine!.transformControls.setMode("scale");
    } else if (event.key === "Escape") {
      Engine.activeEngine!.transformControls.reset();
    }
  }

  onKeydownOccured(event: KeyboardEvent) {
    if (!this._enabled) return;

    if (event.key === "z" && event.shiftKey && event.metaKey) {
      event.preventDefault();
      globalCommandManager.do();
    } else if (
      (event.ctrlKey || event.metaKey) &&
      event.key.toLowerCase() === "z"
    ) {
      event.preventDefault();
      globalCommandManager.undo();
    } else if (event.key === "Backspace" && (event.ctrlKey || event.metaKey)) {
      if (this._selectedModel) {
        globalCommandManager.run(new DeleteCommand(this._selectedModel));
        this.engine!.dispatchCustomEvent("model-removed");

        Engine.activeEngine!.transformControls.detach();
        this._selectedModel = undefined;
      }
    }
  }

  private _savedTransform?: {
    position?: Vector3;
    rotation?: Quaternion;
    scale?: Vector3;
  };

  onModelSelectedOccured(event: any) {
    if (!this._enabled) return;
    const model = event.detail as SugarModel;
    if (model) {
      Engine.activeEngine!.transformControls.enabled = true;
      Engine.activeEngine!.transformControls.attach(model);
      this._selectedModel = model;
    } else {
      Engine.activeEngine!.transformControls.detach();
      Engine.activeEngine!.transformControls.enabled = false;
    }
  }

  private _saveTransform() {
    if (!Engine.activeEngine.transformControls.object) return;
    const object = Engine.activeEngine.transformControls.object;

    this._savedTransform = {
      position: object.position.clone(),
      rotation: object.quaternion.clone(),
      scale: object.scale.clone(),
    };
  }

  onOrbitChange() {
    this._orbitEnabled = true;
  }

  onOrbitEnd() {
    this._orbitEnabled = false;
  }

  onMousedownTransformControls(event: any) {
    this._transformEnabled = true;
    this._orbitControls!.enabled = false;
    this._saveTransform();
  }

  onMouseupTransformControls(event: any) {
    this._transformEnabled = false;
    this._orbitControls!.enabled = true;

    const object = Engine.activeEngine.transformControls.object!;
    globalCommandManager.run(
      new TransformCommand(
        object,
        {
          position: object.position.clone(),
          rotation: object.quaternion.clone(),
          scale: object.scale.clone(),
        },
        this._savedTransform!
      )
    );
    this.engine?.dispatchCustomEvent("transform-changed", { detail: object });
  }

  get enabled(): boolean {
    return super.enabled;
  }

  set enabled(value: boolean) {
    this._orbitControls?.removeEventListener("change", this.onOrbitChange);
    this._orbitControls?.removeEventListener("end", this.onOrbitEnd);
    Engine.activeEngine!.transformControls.removeEventListener(
      "mouseDown",
      this._onMousedownTransformControlsBindInstance
    );
    Engine.activeEngine!.transformControls.removeEventListener(
      "mouseUp",
      this._onMouseupTransformControlsBindInstance
    );
    this.engine!.activeScene!.remove(this._gridHelper);
    if (value) {
      this.engine!.activeScene!.add(this._gridHelper);
      this._orbitControls?.addEventListener(
        "change",
        this._onOrbitChangeInstance
      );

      this._orbitControls?.addEventListener("end", this._onOrbitEndInstace);

      Engine.activeEngine!.transformControls.addEventListener(
        "mouseUp",
        this._onMouseupTransformControlsBindInstance
      );

      Engine.activeEngine!.transformControls.addEventListener(
        "mouseDown",
        this._onMousedownTransformControlsBindInstance
      );
    } else {
    }

    super.enabled = value;
  }
}
